import { useEffect, useState } from 'react';

import { services } from '@/services';

import { isPointsUnavailableError } from './pointsErrors';

/**
 * usePointsGate — 帳號選單「訂閱與用量」列能不能點進點數頁(runtime probe,無 gate 常數)
 * -----------------------------------------------------------------------------
 * probe = M2 `GET /wallet/mentor/status`(docs/141;不打 Mentor,比套餐表便宜)。判準(R299 藏入口族):
 *   - `configured: false` / 503 `MENTOR_NOT_CONFIGURED` / 路由不存在的 404(BE 未部署)→ `disabled`
 *     (這個環境沒有點數功能:那一列維持純顯示,不留點了沒反應的列)。
 *   - 其他錯(網路 / 5xx / 429 / 工作區身分)→ `enabled`:暫時性錯誤不等於沒有功能,頁內誠實顯錯 + 重試。
 *   - **探測中(`unknown`)照樣給點**:等它回來才給點 = 打開選單那一列死一下;先點進去也沒壞處。
 *
 * BE 部署到哪個環境,入口就在哪個環境自己長出來。結果 session 級快取(module scope)。
 */
export type PointsGateState = 'unknown' | 'enabled' | 'disabled';

let cache: PointsGateState = 'unknown';
let inflight: Promise<PointsGateState> | null = null;
const listeners = new Set<(s: PointsGateState) => void>();

function probe(): Promise<PointsGateState> {
  inflight ??= services.pointsService
    .getStatus()
    .then((s): PointsGateState => (s.configured ? 'enabled' : 'disabled'))
    .catch((err: unknown): PointsGateState => {
      if (isPointsUnavailableError(err)) return 'disabled';
      console.warn('[points-gate] probe failed (transient), keep entry clickable', err);
      return 'enabled';
    })
    .then((state) => {
      cache = state;
      inflight = null;
      for (const l of listeners) l(state);
      return state;
    });
  return inflight;
}

export function usePointsGate(): PointsGateState {
  const [state, setState] = useState<PointsGateState>(cache);

  useEffect(() => {
    if (cache !== 'unknown') {
      setState(cache);
      return;
    }
    let alive = true;
    const listener = (s: PointsGateState) => {
      if (alive) setState(s);
    };
    listeners.add(listener);
    void probe();
    return () => {
      alive = false;
      listeners.delete(listener);
    };
  }, []);

  return state;
}

/** 測試用:清 module cache。 */
export function resetPointsGateForTest(): void {
  cache = 'unknown';
  inflight = null;
  listeners.clear();
}
